import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Loader2 } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { UpdateLeaveTypeDTO } from "@/modules/hr/leave/domain/types";
import {
  useLeaveTypeDetailQuery,
  useUpdateLeaveTypeMutation,
} from "../api/queries";
import {
  LeaveTypeEditTour,
  useLeaveTypeEditTour,
} from "../tour/LeaveTypeEditTour";
import { LeaveTypeForm } from "../ui/LeaveTypeForm";

const LIST_SEARCH = {
  offset: 0,
  limit: 20,
  sort: undefined as { field: string; dir: "asc" | "desc" }[] | undefined,
  isActive: undefined as boolean | undefined,
  search: undefined as string | undefined,
};

export function LeaveTypeEditPage() {
  const { id } = useParams({ strict: false });
  const nav = useNavigate();
  const tour = useLeaveTypeEditTour();
  const { data: type, isLoading, error } = useLeaveTypeDetailQuery(id);
  const updateMutation = useUpdateLeaveTypeMutation();

  const handleSubmit = (values: UpdateLeaveTypeDTO) => {
    if (!id) return;
    updateMutation.mutate(
      { id, dto: values },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກການແກ້ໄຂສຳເລັດ");
          nav({ to: "/app/hr/leave/types/$id", params: { id } });
        },
        onError: (err) => {
          toast.error(err?.message ?? "ເກີດຂໍ້ຜິດພາດ");
        },
      },
    );
  };

  if (isLoading || (!type && !error)) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  if (error || !type) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto w-full max-w-4xl py-4">
            <p className="text-destructive">ບໍ່ພົບປະເພດການລາ.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() =>
                nav({ to: "/app/hr/leave/types", search: LIST_SEARCH })
              }
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const handleBack = () => {
    nav({ to: "/app/hr/leave/types/$id", params: { id: type.id } });
  };

  return (
    <>
      <Header />
      <Main>
        <LeaveTypeEditTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="leave-type-edit-back"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ແກ້ໄຂປະເພດການລາ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{type.name}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div
          className="mx-auto w-full max-w-4xl space-y-6"
          data-tourid="leave-type-edit-form"
        >
          <LeaveTypeForm
            mode="edit"
            type={type}
            onSubmit={handleSubmit}
            onCancel={handleBack}
            isSubmitting={updateMutation.isPending}
          />
        </div>
      </Main>
    </>
  );
}
